import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getCommonUrl } from '../../../components/common/common-functions/commonApiUrl/get-common-url';
import { handleDownloadResponse } from '../../../components/common/common-functions/handle-response';
import { ROLES } from '../../../constants/enums/roles';
import getConfig from '../../../helpers/config';
import { CalendarTrajnime } from '../../../interfaces/calendar';
import { IdEntity } from '../../../interfaces/common-interfaces';
import { PaginationReturn } from '../../../interfaces/pagination';
import {
  ActiveTrainingDataProps,
  ActiveTrainingFilters,
  ApplyAsTrainee,
  AttendanceRequest,
  CreateCalendarProps,
  CreateTraining,
  ExpertProps,
  ParticipantInTrainingsProps,
  ParticipantQueryProps,
  ProgramiFundit,
  ProgramTematikTrajnimet,
  ProposedTrainingsProps,
  TraineeByIdProps,
  TraineesOfTrainingProps,
  TrainingByIdParams,
  TrainingConfigurationData,
  TrainingExpertProps,
} from '../../../interfaces/training';
import { getPrepareHeaders } from '../get-prepare-headers';
import { removeEmptyParams } from '../../../components/common/common-functions/common-functions';
import { LlojTrajnimi } from '../../../utilities/enums';
import { NiveliPrioritetitKarta } from '../../../pages/AdministrationOfContinuousEducation/trajnimi-per-pjesemarres/training-level-cards';
import { Key } from 'react';

const { test } = getConfig();

export interface TrainingFiltersProps {
  tema?: Key;
  kategori?: string;
  nenKategori?: string;
  statusTrajnimi?: string;
  vitiAkademikId?: number;
  regjistruar?: boolean;
  eshteTrajnues?: boolean;
  dataFilterFillimi?: string;
  dataFilterMbarimi?: string;
  kategoria?: string;
  llojTrajnimi?: LlojTrajnimi;
  id?: number;
  programTematikId?: number;
  programTematikKalendarId?: number;
  moduliId?: number;
}

export interface ManageTrainingApplicationsParams {
  id: number;
  aplikimSiPanelist?: boolean;
  aplikimSiPjesemarres?: boolean;
}

export type ActiveTrainingType = PaginationReturn<TraineeByIdProps>;

export const trajnimetApi = createApi({
  reducerPath: 'trajnimetApi',
  baseQuery: fetchBaseQuery({
    baseUrl: test.baseUrl,
    prepareHeaders: getPrepareHeaders,
  }),
  tagTypes: [
    'Trajnime',
    'Regjistrime',
    'VitiAktual',
    'Participants',
    'Trajnimi',
  ],
  endpoints: builder => ({
    createTrainings: builder.mutation<void, CreateTraining>({
      query: ({ programTematikId, ...body }) => {
        return {
          url: `/trajnimi/program-tematik/${programTematikId}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Trajnime'],
    }),
    getAllTrainingsByProgramTematik: builder.query<
      ProgramTematikTrajnimet,
      ActiveTrainingFilters
    >({
      query: ({ id, ...args }) =>
        getCommonUrl(args, `/trajnimi/program-tematik/${id}`),
      providesTags: ['Trajnime'],
    }),
    getAllSimplifiedTrainings: builder.query<
      ActiveTrainingType,
      ActiveTrainingFilters
    >({
      query: args => getCommonUrl(args, '/trajnimi/kalendar'),
      providesTags: ['Trajnime'],
    }),
    getAllSimplifiedTrainingForParticipant: builder.query<
      PaginationReturn<TrainingConfigurationData>,
      { page: number; size: number; tema: string | undefined }
    >({
      query: args => {
        let url = `/trajnimi/pjesemarres/simplified?page=${args.page}&sort=tema:rendRrites`;

        if (args.size) {
          url += `&size=${args.size}`;
        }
        if (args.tema) {
          url += `&filter=tema:ilike:${args.tema}`;
        }

        return {
          url,
        };
      },
    }),

    getAllCalendarTrainings: builder.query<
      CalendarTrajnime,
      ActiveTrainingFilters
    >({
      query: ({ id, ...args }) => getCommonUrl(args, `trajnimi/kalendar/${id}`),
      providesTags: ['Trajnime'],
    }),
    createCalendarTopics: builder.mutation<void, void>({
      query: () => ({
        url: `/trajnimi/krijo-kalendar`,
        method: 'PUT',
      }),
      invalidatesTags: ['Trajnime'],
    }),
    createCalendarTrainings: builder.mutation<void, CreateCalendarProps>({
      query: ({ id, ...body }) => {
        return {
          url: `/trajnimi/kalendar/${id}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Trajnime'],
    }),
    updateTrainings: builder.mutation<
      IdEntity,
      Partial<ActiveTrainingDataProps>
    >({
      query: ({ id, ...body }) => {
        return {
          url: `/trajnimi/${id}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['Trajnime', 'VitiAktual'],
    }),
    deleteTrainings: builder.mutation<string, number>({
      query: arg => ({
        url: `/trajnimi/${arg}`,
        method: 'Delete',
      }),
      invalidatesTags: ['Trajnime'],
    }),
    prepareActiveYearProgramTopics: builder.mutation<void, void>({
      query: () => ({
        url: '/trajnimi/pergatit-program-tematik',
        method: 'PUT',
      }),
      invalidatesTags: ['Trajnime'],
    }),
    publishProgramTematik: builder.mutation<string, void>({
      query: () => ({
        url: '/trajnimi/publiko-program-tematik',
        method: 'PUT',
        responseHandler: 'text',
      }),
      invalidatesTags: ['Trajnime'],
    }),
    prepareActiveCalendarTopics: builder.mutation<void, number>({
      query: id => ({
        url: `/trajnimi/publiko-kalendar/${id}`,
        method: 'PUT',
        responseHandler: response => response.text(),
      }),
      invalidatesTags: ['Trajnime'],
    }),
    getAllTrainingsForParticipants: builder.query<
      ActiveTrainingType,
      ActiveTrainingFilters
    >({
      query: args =>
        getCommonUrl(args, '/trajnimi/program-tematik/pjesemarres'),
      providesTags: ['Regjistrime'],
    }),
    getAllCalendarTrainingsForParticipants: builder.query<
      ActiveTrainingType,
      ActiveTrainingFilters
    >({
      query: args => getCommonUrl(args, '/trajnimi/kalendar/pjesemarres'),
      providesTags: ['Regjistrime', 'Trajnime'],
    }),
    updateSubscribeTrainings: builder.mutation<
      void,
      { id: number; niveliPrioritetit?: string }
    >({
      query: ({ id, niveliPrioritetit }) => {
        const url = niveliPrioritetit
          ? `/trajnimi/${id}/regjistrohu?niveliPrioritetit=${niveliPrioritetit}`
          : `/trajnimi/${id}/regjistrohu`;

        return {
          url,
          method: 'POST',
        };
      },
      invalidatesTags: ['Trajnimi', 'VitiAktual', 'Regjistrime'],
    }),
    updateUnsubscribeTrainings: builder.mutation<void, number>({
      query: id => {
        return {
          url: `/trajnimi/${id}/anullo-regjistrimin`,
          method: 'PUT',
        };
      },
      invalidatesTags: ['Regjistrime', 'Trajnimi'],
    }),
    applyAsTrainee: builder.mutation<void, ApplyAsTrainee>({
      query: ({ id, roliTrajnues, ekspertiza }) => ({
        url: `/trajnimi/${id}/apliko-si-trajnues`,
        method: 'PUT',
        params: removeEmptyParams({
          roliTrajnues,
          ekspertiza,
        }),
      }),
      invalidatesTags: ['Regjistrime', 'VitiAktual', 'Trajnimi'],
    }),
    getTraineeById: builder.query<TrainingByIdParams, IdEntity>({
      query: args => ({
        url: `trajnimi/program-tematik/${args.id}/aplikuar-si-ekspert/sipas-perdoruesit-te-loguar`,
      }),
      providesTags: ['Regjistrime'],
    }),
    getTrainingsById: builder.query<TraineeByIdProps, IdEntity>({
      query: args =>
        args.role === ROLES.pjesmarres
          ? `/trajnimi/kalendar/${args.id}/pjesemarres`
          : `/trajnimi/${args.id}`,
      providesTags: ['VitiAktual', 'Trajnimi'],
    }),
    updateTraineesOfTraining: builder.mutation<
      void,
      {
        id: number | undefined;
        body: TraineesOfTrainingProps[];
      }
    >({
      query: ({ id, ...body }) => {
        return {
          url: `/trajnimi/${id}/trajnues`,
          method: 'PUT',
          body: body.body,
        };
      },
      invalidatesTags: ['VitiAktual'],
    }),
    proposeTrainings: builder.mutation<void, TrainingConfigurationData>({
      query: body => {
        return {
          url: `/trajnimi/propozo`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Trajnime'],
    }),
    getProposedTrainings: builder.query<
      PaginationReturn<ProposedTrainingsProps>,
      ActiveTrainingFilters
    >({
      query: args => getCommonUrl(args, '/trajnimi/propozuar'),
      providesTags: ['Trajnime'],
    }),
    updateApprovalOfTraining: builder.mutation<void, number>({
      query: id => ({
        url: `/trajnimi/${id}/aprovo`,
        method: 'PUT',
      }),
      invalidatesTags: ['Trajnime'],
    }),
    getProposedTrainingsForParticipant: builder.query<
      ActiveTrainingType,
      ActiveTrainingFilters
    >({
      query: args => getCommonUrl(args, '/trajnimi/propozuar-per-pjesemarres'),
      providesTags: ['Trajnime'],
    }),
    createTrainingAttendance: builder.mutation<void, AttendanceRequest>({
      query: ({ id, data, body }) => {
        return {
          url: `/trajnimi/${id}/prezenca?data=${data}`,
          method: 'POST',
          body,
        };
      },
    }),
    getParticipantsAttendanceMaterial: builder.query<Blob, IdEntity>({
      query: arg => {
        return {
          url: `/trajnimi/${arg.id}/eksport-pjesemarres`,
          responseHandler: handleDownloadResponse,
        };
      },
    }),
    updateTrainingParticipantsAttendance: builder.mutation<
      void,
      { id: number; body: Array<number> }
    >({
      query: ({ id, body }) => ({
        url: `/trajnimi/${id}/pjesemarres`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Participants'],
    }),
    getParticipantsInTrainingById: builder.query<
      ParticipantInTrainingsProps,
      ParticipantQueryProps
    >({
      query: args => {
        let url = `/pjesemarres-ne-trajnime/${args.trajnimiId}?size=300&sort=personiEmri:asc`;

        if (args.emriMbiemri) {
          url += `&filter=emriMbiemri:ilike:${args.emriMbiemri}`;
        }

        if (args.personiEmri) {
          url += `&filter=personiEmri:ilike:${args.personiEmri}`;
        }

        if (args.funksioni) {
          url += `&filter=funksioni:eq:${args.funksioni}`;
        }
        if (args.institucioniId) {
          url += `&filter=institucioniId:eq:${args.institucioniId}`;
        }

        return {
          url,
        };
      },
      providesTags: ['Participants'],
    }),
    getExpertInTraining: builder.query<
      PaginationReturn<ExpertProps>,
      ParticipantQueryProps
    >({
      query: args =>
        getCommonUrl(
          { ...args },
          `trajnimi/program-tematik/${args.trajnimiId}/aplikuar-si-ekspert?size=300&sort=emriPjesemarres:asc`,
        ),
    }),
    finishTraining: builder.mutation<void, number>({
      query: id => ({
        url: `/trajnimi/${id}/aprovo`,
        method: 'PUT',
      }),
      invalidatesTags: ['VitiAktual'],
    }),
    getTrainingsForExpert: builder.query<
      PaginationReturn<TrainingExpertProps>,
      ActiveTrainingFilters
    >({
      query: args => getCommonUrl(args, '/trajnimi/kalendar/ekspert'),
      providesTags: ['Trajnimi'],
    }),
    getTrajnimeProgramTematikFundit: builder.query<
      PaginationReturn<ProgramiFundit>,
      Partial<ProgramiFundit & { page: number; size: number }>
    >({
      query: params => ({
        url: getCommonUrl(params, '/trajnimi/trajnime-program-tematik'),
      }),
    }),
    manageTrainingApplications: builder.mutation<
      number,
      ManageTrainingApplicationsParams
    >({
      query: ({ id, ...body }) => ({
        url: `/trajnimi/menaxho-aplikime/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['VitiAktual'],
    }),
    updateAprovoPanelist: builder.mutation<
      void,
      { id: number; status: string }
    >({
      query: ({ id, status }) => ({
        url: `/trajnimi/aprovo-panelist/${id}?statusTrajnues=${status}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Trajnime', 'VitiAktual'],
    }),
    getPriorityLevelCards: builder.query<NiveliPrioritetitKarta, number>({
      query: id => `/trajnimi/niveli-prioritetit/karta/${id}`,
      providesTags: ['Trajnime', 'VitiAktual', 'Trajnimi'],
    }),
  }),
});

export const {
  useGetAllTrainingsByProgramTematikQuery,
  useLazyGetAllSimplifiedTrainingsQuery,
  useLazyGetAllSimplifiedTrainingForParticipantQuery,
  useUpdateTrainingsMutation,
  useDeleteTrainingsMutation,
  usePrepareActiveYearProgramTopicsMutation,
  usePublishProgramTematikMutation,
  useGetAllTrainingsForParticipantsQuery,
  useGetAllCalendarTrainingsForParticipantsQuery,
  useUpdateSubscribeTrainingsMutation,
  useUpdateUnsubscribeTrainingsMutation,
  useApplyAsTraineeMutation,
  useGetTraineeByIdQuery,
  useGetTrainingsByIdQuery,
  useLazyGetTrainingsByIdQuery,
  useUpdateTraineesOfTrainingMutation,
  useProposeTrainingsMutation,
  useGetProposedTrainingsForParticipantQuery,
  useGetAllCalendarTrainingsQuery,
  useCreateCalendarTopicsMutation,
  usePrepareActiveCalendarTopicsMutation,
  useCreateCalendarTrainingsMutation,
  useLazyGetAllCalendarTrainingsQuery,
  useGetProposedTrainingsQuery,
  useUpdateApprovalOfTrainingMutation,
  useCreateTrainingAttendanceMutation,
  useLazyGetParticipantsAttendanceMaterialQuery,
  useUpdateTrainingParticipantsAttendanceMutation,
  useFinishTrainingMutation,
  useGetTrainingsForExpertQuery,
  useCreateTrainingsMutation,
  useGetParticipantsInTrainingByIdQuery,
  useGetExpertInTrainingQuery,
  useLazyGetTrajnimeProgramTematikFunditQuery,
  useManageTrainingApplicationsMutation,
  useUpdateAprovoPanelistMutation,
  useGetPriorityLevelCardsQuery,
} = trajnimetApi;
